import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useContext, useMemo } from "react";
import AuthContext from "../store/auth-context";
import { NavLink } from "react-router-dom";
import "./SideNav.css";
import { FaUser } from "react-icons/fa";

function DashboardRootLayout() {
  const [isActive, setIsActive] = useState(false);
  const authCtx = useContext(AuthContext);
  const isIpdEnabled = authCtx.hospitalData?.boolIpdAccess;
  const role = authCtx.role;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const toggleSidebar = () => {
    setIsActive(!isActive);
  };

  const logoutHandler = () => {
    authCtx.onLogout();
    navigate('/');
  };

  const handleNavLinkClick = () => {
    if (window.innerWidth <= 768) {
      setIsActive(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const menuConfig = useMemo(() => [
    {
      to: "/dashboard",
      icon: "fa-home",
      label: t("Dashboard"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_GUESTDOCTOR", "ROLE_RECEPTIONIST"],
      end: true
    },
    {
      to: "appointments",
      icon: "fa-user-md",
      label: t("APPOINTMENT"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_RECEPTIONIST"]
    },
    {
      to: "checklist",
      icon: "fa-stethoscope",
      label: t("CHECKUP LIST"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_GUESTDOCTOR"]
    },
    {
      to: "/dashboard/patients",
      icon: "fa-book",
      label: t("PATIENTS"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_GUESTDOCTOR", "ROLE_NURSE", "ROLE_RECEPTIONIST"]
    },
    {
      to: "/dashboard/ipd",
      icon: "fa-bed",
      label: t("IPD"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_GUESTDOCTOR", "ROLE_NURSE", "ROLE_RECEPTIONIST"],
      showIf: !!isIpdEnabled
    },
    {
      to: "/dashboard/users",
      icon: "fa-group",
      label: t("STAFF"),
      roles: ["ROLE_ADMIN"]
    },
    {
      to: "/dashboard/medicines",
      icon: "fa-medkit",
      label: t("PHARMACY"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_GUESTDOCTOR", "ROLE_PHARMACIST", "ROLE_NURSE"]
    },
    {
      to: "/dashboard/billing",
      icon: "fa-file-text",
      label: t("OPD BILLING"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR", "ROLE_ACCOUNTANT", "ROLE_RECEPTIONIST"]
    },
    {
      to: "/dashboard/reports",
      icon: "fa-bar-chart",
      label: t("OPD REPORTS"),
      roles: ["ROLE_ADMIN", "ROLE_DOCTOR"]
    },
    {
      to: "/dashboard/config",
      icon: "fa-cog",
      label: t("CONFIG"),
      roles: ["ROLE_ADMIN"]
    }
  ], [t, isIpdEnabled]);

  const visibleMenuItems = useMemo(() =>
    menuConfig.filter(item => {
      const hasRole = item.roles.includes(role);
      const conditionalShow = item.showIf !== undefined ? item.showIf : true;
      return hasRole && conditionalShow;
    }), [menuConfig, role]
  );

  return (
    <>
      <div className={`side-navbar ${isActive ? "active-nav" : ""} d-flex flex-column`} id="sidebar">
        <div className="d-flex justify-content-end d-md-none p-2">
          <button className="btn text-white border-0" onClick={toggleSidebar}>
            <i className="fa fa-times" style={{ fontSize: '1.2rem' }}></i>
          </button>
        </div>
        <div className="user-section">
          <div className="user-avatar-circle">
            <FaUser />
          </div>
          <h5 className="user-display-name">{authCtx.userName || "Admin"}</h5>
          <p className="user-display-role">{role ? role.replace("ROLE_", "").toLowerCase() : "Staff"}</p>
        </div>

        <ul className="nav flex-column text-white w-100 flex-grow-1">
          {visibleMenuItems.map((item, index) => (
            <li className="nav-item" key={index}>
              <NavLink
                className={({ isActive }) => isActive ? "nav-link active-link" : "nav-link"}
                to={item.to}
                end={item.end}
                onClick={handleNavLinkClick}
              >
                <i className={`fa ${item.icon}`}></i>
                <p>{item.label}</p>
              </NavLink>
            </li>
          ))}

          <li className="nav-item mt-auto mb-3">
            <button className="nav-link w-100 border-0 bg-transparent" onClick={logoutHandler} style={{ cursor: 'pointer' }}>
              <i className="fa fa-sign-out"></i>
              <p>{t("LOGOUT")}</p>
            </button>
          </li>
        </ul>
      </div>

      <div className={`my-container ${isActive ? "active-cont" : ""}`}>
        {isActive && window.innerWidth <= 768 && (
          <div className="sidebar-overlay" onClick={toggleSidebar}></div>
        )}
        <div className="px-md-5 px-1 d-flex align-items-center mobile-header-toggle" style={{ height: window.innerWidth <= 768 ? '42px' : 'auto' }}>
          <button className="btn border-0" id="menu-btn" onClick={toggleSidebar}>
            <span className="fa fa-bars"></span>
          </button>
        </div>
        <Outlet />
      </div>
    </>
  );
}

export default DashboardRootLayout;
